"use client";
import React, { useEffect, useState } from "react";

import AnalyticsSidebar from "../../AnalyticsSidebar";
import { Navbar } from "../../../../components/navbar";
import { Footer } from "../../../../components/footer";
import FormInput from "../../../../components/FormInput";

import styles from "../css/ReturnRate.module.css";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { getReturnRate } from "../../../../api/analytics/analyticsApi.js";

const COLORS = ["#A855F7", "#D8B4FE", "#E9D5FF"];

const renderCalloutLabel = (props) => {

  const RAD = Math.PI / 180;
  const {
    cx, cy, midAngle, outerRadius,
  } = props;

  const sin = Math.sin(-RAD * midAngle);
  const cos = Math.cos(-RAD * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const tx = cx + (outerRadius + 32) * cos;
  const ty = cy + (outerRadius + 32) * sin;
  const ex = tx + (cos >= 0 ? 28 : -28);
  const ey = ty;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  const pct = Math.round(props?.payload?.percentage ?? 0);

  return (
    <g>
      <path
        d={`M ${sx},${sy} L ${ex},${ey}`}
        stroke="#6B7280"
        strokeWidth={2}
        fill="none"
        markerEnd="url(#rr-arrow)"
      />
      {/* Place the percentage slightly beyond the arrow tip and centered on the line */}
      <text
        x={ex + (cos >= 0 ? 8 : -8)}
        y={ey + 4}
        textAnchor={textAnchor}
        fontWeight={800}
        fill="#111827"
      >
        {`${pct}%`}
      </text>
    </g>
  );
};

export default function ReturnRate() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const from = fromDate && toDate ? new Date(fromDate) : undefined;
        const to = fromDate && toDate ? new Date(toDate) : undefined;

        const rows = await getReturnRate({
          fromDate: from,
          toDate: to,
        });

        if (!isMounted) return;

        const chartData = (rows || []).map((row, _, all) => ({
          name: row.categoryName,
          value: Number(row.returnedCost ?? 0),
          percentage: Number(row.percentage ?? 0),
          allValues: all.map((r) => Number(r.returnedCost ?? 0)),
        }));

        setData(chartData);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Failed to load return rate");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if ((fromDate && toDate) || (!fromDate && !toDate)) {

      load();
    }

    return () => {
      isMounted = false;
    };
  }, [fromDate, toDate]);

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar backTo="/analytics" />

        <div className={`${styles.contentArea} analytics-content`}>
          <AnalyticsSidebar />
          <div className={styles.contentAreaFull}>
            <section className={styles.panelArea}>
              <h2 className={styles.pageTitle}>Return Rate</h2>

              <div className={styles.filtersRow}>
                <div className={styles.inlineFilter}>
                  <span>From</span>
                  <FormInput
                    type="date"
                    name="fromDate"
                    value={fromDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setFromDate(value);
                    }}
                  />
                </div>
                <div className={styles.inlineFilter}>
                  <span>To</span>
                  <FormInput
                    type="date"
                    name="toDate"
                    value={toDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setToDate(value);
                    }}
                  />
                </div>

              </div>

              <div className={styles.chartCard}>
                {error && (
                  <div className="text-red-500 text-sm mb-2">{error}</div>
                )}
                {!loading && !error && (!data || data.length === 0) ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No data available at the moment
                  </p>
                ) : (
                  <div className={styles.chartInner}>
                    <ResponsiveContainer width="100%" height={420}>
                      <PieChart>

                        <defs>
                          <marker
                            id="rr-arrow"
                            markerWidth="8"
                            markerHeight="8"
                            refX="7"
                            refY="4"
                            orient="auto-start-reverse"
                          >
                            <path d="M0,1 L8,4 L0,7 z" fill="#6B7280" />
                          </marker>
                        </defs>
                        <Legend
                          verticalAlign="top"
                          align="center"
                          wrapperStyle={{ color: '#000', fontWeight: 600 }}
                        />
                        <Tooltip
                          formatter={(val, name, payload) => {
                            const amount = Number(val ?? 0);
                            const amountStr = `R ${amount.toLocaleString('en-ZA')}`;
                            const ratePct = Math.round(payload?.payload?.percentage ?? 0);
                            const label = `${name} (${ratePct}%)`;
                            return [amountStr, label];
                          }}
                        />
                        <Pie
                          data={data}
                          dataKey="value"

                          nameKey="name"
                          cx="50%"
                          cy="55%"
                          outerRadius={120}
                          labelLine={false}
                          label={renderCalloutLabel}
                          isAnimationActive={true}
                          animationDuration={600}
                          animationEasing="linear"
                        >
                          {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}